import React from 'react';
import { OrderDetails } from '../services/OrderService';
import './OrderDisplay.css';

// Store information mapping
interface StoreInfo {
  name: string;
  address: string;
  phone: string;
}

const RETAILER_ID_TO_STORE: Record<string, StoreInfo> = {
  // Olney
  '60e98181-4255-48d4-a3dd-8dde829d0620': {
    name: 'SWEETSPOT OLNEY',
    address: '18070 Georgia Ave, Olney MD 20832',
    phone: '(301) 774-0840'
  },
  // West Hartford
  '2ca5693a-0ce8-421a-8e6b-f07c2b498a29': {
    name: 'SWEETSPOT WEST HARTFORD',
    address: '2 Park Rd, West Hartford, CT 06119',
    phone: '(860) 838-8330'
  },
  // Stamford
  '9ac9d5ad-5e4e-41a3-b37f-6da013b423ae': {
    name: 'SWEETSPOT STAMFORD',
    address: '111 High Ridge Rd, Stamford, CT 06905',
    phone: '(860) 321-1291'
  },
  // Voorhees Medical
  '8b4140a0-14d5-4472-81ef-773707aa1756': {
    name: 'SWEETSPOT VOORHEES',
    address: '903 White Horse Rd, Vorhees, NJ 08043',
    phone: '(856) 882-3481'
  },
  // Voorhees Recreational
  '3c85c2b4-2228-43e5-9ec0-df7a96a0a02a': {
    name: 'SWEETSPOT VOORHEES',
    address: '903 White Horse Rd, Vorhees, NJ 08043',
    phone: '(856) 882-3481'
  },
  // River Edge
  '9fc171e5-b11a-4434-910f-08253f1a6aec': {
    name: 'SWEETSPOT RIVER EDGE',
    address: '75 State Route 4, River Edge, NJ 07661',
    phone: '(201) 331-0140'
  },
  // Exeter
  '7b0dfd1a-101e-4641-b34e-413097825c49': {
    name: 'SWEETSPOT EXETER',
    address: '560 S. County Trail, Exeter, RI 02822',
    phone: '(401) 268-5126'
  },
  // Essex Junction
  'dcaee184-ca72-4e9f-a5fa-9c24613a0882': {
    name: 'SWEETSPOT ESSEX JUNCTION',
    address: '139 Pearl St, Essex Junction, VT 05452',
    phone: '(802) 871-5895'
  },
  // Mt Olive
  '4a4fa6e9-11e8-493e-a560-c7dd73ca0c58': {
    name: 'SWEETSPOT MT OLIVE',
    address: '41 US-46, Budd Lake, NJ 07828',
    phone: '(973) 527-3077'
  },
  // Hamilton
  '44788b18-7be4-45af-bb6e-d9563bc9efb4': {
    name: 'SWEETSPOT HAMILTON',
    address: '147 Sloan Ave, Trenton, NJ 08619',
    phone: '(609) 689-4899'
  }
};

const DISPENSARY_NAME_MAPPING: Record<string, StoreInfo> = {
  'olney': RETAILER_ID_TO_STORE['60e98181-4255-48d4-a3dd-8dde829d0620'],
  'west hartford': RETAILER_ID_TO_STORE['2ca5693a-0ce8-421a-8e6b-f07c2b498a29'],
  'stamford': RETAILER_ID_TO_STORE['9ac9d5ad-5e4e-41a3-b37f-6da013b423ae'],
  'voorhees': RETAILER_ID_TO_STORE['8b4140a0-14d5-4472-81ef-773707aa1756'],
  'river edge': RETAILER_ID_TO_STORE['9fc171e5-b11a-4434-910f-08253f1a6aec'],
  'exeter': RETAILER_ID_TO_STORE['7b0dfd1a-101e-4641-b34e-413097825c49'],
  'essex junction': RETAILER_ID_TO_STORE['dcaee184-ca72-4e9f-a5fa-9c24613a0882'],
  'mt olive': RETAILER_ID_TO_STORE['4a4fa6e9-11e8-493e-a560-c7dd73ca0c58'],
  'hamilton': RETAILER_ID_TO_STORE['44788b18-7be4-45af-bb6e-d9563bc9efb4']
};

function getStoreInfoFromOrder(orderData: { dispensaryName?: string; retailerId?: string }): StoreInfo {
  // Try retailer ID first (most precise)
  if (orderData.retailerId && RETAILER_ID_TO_STORE[orderData.retailerId]) {
    return RETAILER_ID_TO_STORE[orderData.retailerId];
  }
  
  // Try dispensary name matching
  if (orderData.dispensaryName) {
    const dispensaryKey = orderData.dispensaryName.toLowerCase().trim();
    if (DISPENSARY_NAME_MAPPING[dispensaryKey]) {
      return DISPENSARY_NAME_MAPPING[dispensaryKey];
    }
    
    // Try partial matching (remove "sweetspot" prefix if present)
    const withoutSweetspot = dispensaryKey.replace(/^sweetspot\s+/, '');
    if (DISPENSARY_NAME_MAPPING[withoutSweetspot]) {
      return DISPENSARY_NAME_MAPPING[withoutSweetspot];
    }
    
    // Try finding store name within dispensary name
    for (const [key, storeInfo] of Object.entries(DISPENSARY_NAME_MAPPING)) {
      if (dispensaryKey.includes(key) || key.includes(dispensaryKey)) {
        return storeInfo;
      }
    }
  }
  
  // Default to Olney if no match found
  return RETAILER_ID_TO_STORE['60e98181-4255-48d4-a3dd-8dde829d0620'];
}

interface OrderDisplayProps {
  order: OrderDetails;
  className?: string;
  retailerId?: string; // Add retailerId to help identify store
}

export const OrderDisplay: React.FC<OrderDisplayProps> = ({ order, className, retailerId }) => {
  // Get store information dynamically
  const storeInfo = getStoreInfoFromOrder({
    dispensaryName: order.dispensaryName,
    retailerId: retailerId
  });
  
  // Format price helper for order summary (subtotal, total, etc.) - these come in dollars
  const formatPrice = (price: number | null | undefined): string => {
    if (price === null || price === undefined || isNaN(price)) {
      return '$0.00';
    }
    return `$${price.toFixed(2)}`;
  };

  // Format item price helper - individual item prices come in cents and need conversion
  const formatItemPrice = (price: number | null | undefined): string => {
    if (price === null || price === undefined || isNaN(price)) {
      return '$0.00';
    }
    
    // Individual item prices from Dutchie come in cents, convert to dollars
    // Check if price is likely in cents format (whole number >= 100)
    let adjustedPrice = price;
    if (price >= 100 && price % 1 === 0) {
      adjustedPrice = price / 100;
    }
    
    return `$${adjustedPrice.toFixed(2)}`;
  };

  // Format order status for display
  const formatOrderStatus = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'Processing';
      case 'closed':
      case 'completed':
      case 'complete':
        return 'Completed';
      case 'pending':
        return 'Processing';
      case 'confirmed':
        return 'Confirmed';
      case 'ready':
        return 'Ready for Pickup';
      case 'cancelled':
      case 'canceled':
        return 'Cancelled';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
  };

  // Get status styling class
  const getStatusClass = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'open':
      case 'pending':
        return 'status-processing';
      case 'closed':
      case 'completed':
      case 'complete':
        return 'status-completed';
      case 'confirmed':
      case 'ready':
        return 'status-confirmed';
      case 'cancelled':
      case 'canceled':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  };

  // Format payment method for display
  const formatPaymentMethod = (paymentMethod: string): string => {
    switch (paymentMethod?.toUpperCase()) {
      case 'DUTCHIE_PAY':
        return 'Online Payment';
      default:
        return paymentMethod || 'Unknown';
    }
  };

  return (
    <div className={`order-display ${className || ''}`}>
      {/* Store Header */}
      <div className="store-header">
        <div className="store-name">{storeInfo.name}</div>
        <div className="store-address">{storeInfo.address}</div>
        <div className="store-phone">{storeInfo.phone}</div>
      </div>

      {/* Order Header */}
      <div className="order-header">
        <div className="order-info-stack">
          <div className="order-title">
            <span>YOUR ORDER</span>
            <span className="order-number">#{order.orderNumber}</span>
          </div>
          <div className="order-title">
            <span>{new Date(order.createdAt).toLocaleDateString()} at {new Date(order.createdAt).toLocaleTimeString()}</span>
          </div>
          <div className="order-title">
            <span>{order.pickup ? 'Pickup' : order.delivery ? 'Delivery' : 'Unknown'} • {' '}
            {order.medical ? 'Medical' : order.recreational ? 'Recreational' : 'Unknown'} Pricing</span>
          </div>
        </div>
        <div className={`order-status ${getStatusClass(order.status)}`}>
          {formatOrderStatus(order.status)}
        </div>
      </div>

      {/* Order Content */}
      <div className="order-content">
        {/* Order Items */}
        <div className="order-items">
          <div className="items-header">Items Ordered ({order.items.length})</div>
          {order.items.map((item, index) => (
            <div key={`${item.productId}-${item.option}-${index}`} className="order-item">
              <div className="order-item-content">
                {/* Item Details */}
                <div className="order-item-details">
                  <div className="order-item-name">{item?.product?.name || 'Unknown Product'}</div>
                  <div className="order-item-brand">
                    BRAND: <span className="order-item-brand-value">{item?.product?.brand?.name || 'Unknown Brand'}</span>
                  </div>
                  <div className="order-item-option">
                    WEIGHT: <span className="order-item-option-value">{item?.option || 'N/A'}</span>
                  </div>
                  <div className="order-item-price">
                    {formatItemPrice(item.price)} each
                  </div>
                </div>

                {/* Quantity and Total */}
                <div className="order-item-summary">
                  <div className="order-item-quantity">
                    Qty: {item.quantity}
                  </div>
                  <div className="order-item-total">
                    {formatItemPrice(item.subtotal)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="order-summary">
          <div className="summary-header">Order Summary</div>
          
          <div className="order-summary-row">
            <span>Subtotal:</span>
            <span>{formatPrice(order.subtotal)}</span>
          </div>
          
          {order.discounts && order.discounts.total > 0 && (
            <div className="order-summary-row discount">
              <span>Discounts:</span>
              <span>-{formatPrice(order.discounts.total)}</span>
            </div>
          )}
          
          {order.taxes && ((order.taxes.cannabis || 0) + (order.taxes.sales || 0)) > 0 && (
            <div className="order-summary-row">
              <span>Taxes:</span>
              <span>{formatPrice((order.taxes.cannabis || 0) + (order.taxes.sales || 0))}</span>
            </div>
          )}
          
          {order.fees && ((order.fees.delivery || 0) + (order.fees.processing || 0) + (order.fees.excise || 0)) > 0 && (
            <div className="order-summary-row">
              <span>Fees:</span>
              <span>{formatPrice((order.fees.delivery || 0) + (order.fees.processing || 0) + (order.fees.excise || 0))}</span>
            </div>
          )}
          
          <div className="order-summary-total">
            <span>Total Paid:</span>
            <span>{formatPrice(order.total)}</span>
          </div>
        </div>

        {/* Payment Method */}
        <div className="payment-method">
          <span className="payment-label">Payment Method:</span>
          <span className="payment-value">{formatPaymentMethod(order.paymentMethod)}</span>
        </div>
      </div>
    </div>
  );
};

export default OrderDisplay;